import { useStore } from "@/shared/store/useStore.js";
import { useIlfDlfResponse } from "@/hooks/useIlfDlfFetcher.js";

import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import personalStore from "@/modules/Quote/store/personalStore.js";
import classificationStore from "@/modules/Quote/store/classificationStore.js";
import coverageStore from "@/modules/Quote/store/coverageStore.js";
import questionsStore from "@/modules/Quote/store/questionsStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import signupStore from "@/modules/Auth/store/signupStore.js";
import paymentOrderStore from "@/modules/Payment/store/paymentOrderStore.js";
import bindStore from "@/modules/Payment/store/bindStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import specialityStore from "@/modules/Quote/store/specialityStore.js";

import { computeDerivedValues } from "./derivedValues.js";

/**
 * The single hook every page calls when it needs a derived value
 * (`quoteStripPremium`, `snapshotLimits`, `paymentAmountDue`,
 * `headerUserInitials`, etc.).
 *
 *   const { quoteStripPremium, snapshotLimits, isReferral } = useQuoteSnapshot();
 *
 * Subscribes to every store the underlying pure `computeDerivedValues`
 * needs, re-runs when any of them change, and returns the same shape
 * QuoteContext used to expose. Pages don't import the individual stores
 * for derived data — they just call this hook.
 */
export function useQuoteSnapshot() {
  // Form / step
  const step    = useStore(submissionStore, (s) => s.step);
  const zip     = useStore(quoteFormStore, (s) => s.zip);
  const effDate = useStore(quoteFormStore, (s) => s.effDate);
  const pa2Subs = useStore(classificationStore, (s) => s.pa2Subs);

  // Personal / license / practice
  const firstName        = useStore(personalStore, (s) => s.firstName);
  const lastName         = useStore(personalStore, (s) => s.lastName);
  const practiceAddress1 = useStore(personalStore, (s) => s.practiceAddress1);
  const practiceAddress2 = useStore(personalStore, (s) => s.practiceAddress2);
  const practiceCity     = useStore(personalStore, (s) => s.practiceCity);
  const practiceState    = useStore(personalStore, (s) => s.practiceState);
  const practiceZip      = useStore(personalStore, (s) => s.practiceZip);

  // Registration (still useful on the Review page if reg* came via signup)
  const regEmail = useStore(signupStore, (s) => s.regEmail);
  const regPhone = useStore(signupStore, (s) => s.regPhone);

  // API responses + loading flags
  const ilfDlfResponse        = useIlfDlfResponse();
  const paymentOrderDetails   = useStore(paymentOrderStore, (s) => s.paymentOrderDetails);
  const paymentOrderLoading   = useStore(paymentOrderStore, (s) => s.paymentOrderLoading);
  const calculateRatingResult = useStore(coverageStore, (s) => s.calculateRatingResult);
  const calculateRatingLoading = useStore(coverageStore, (s) => s.calculateRatingLoading);
  const flowSubmissionId       = useStore(submissionStore, (s) => s.flowSubmissionId);
  const submissionQuestionGroups = useStore(questionsStore, (s) => s.submissionQuestionGroups);
  const underwritingAnswers      = useStore(questionsStore, (s) => s.underwritingAnswers);
  const hiddenQuestionIds        = useStore(questionsStore, (s) => s.hiddenQuestionIds);

  // Bound confirmation
  const boundPolicyNumberFromInvoice = useStore(bindStore, (s) => s.boundPolicyNumberFromInvoice);
  const boundTotalAmount             = useStore(bindStore, (s) => s.boundTotalAmount);

  // Header
  const insuredProfile = useStore(insuredProfileStore, (s) => s.insuredProfile);

  return computeDerivedValues({
    step, zip, effDate, pa2Subs,
    firstName, lastName,
    regEmail, regPhone,
    practiceAddress1, practiceAddress2, practiceCity, practiceState, practiceZip,
    ilfDlfResponse,
    paymentOrderDetails, paymentOrderLoading,
    calculateRatingResult, calculateRatingLoading,
    flowSubmissionId,
    submissionQuestionGroups,
    underwritingAnswers,
    hiddenQuestionIds,
    boundPolicyNumberFromInvoice,
    boundTotalAmount,
    insuredProfile,
  });
}
